// ============================================
// Register Page
// ============================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Zap } from 'lucide-react';
import { api } from '../../lib/api';

interface TeamOption {
  id: string;
  name: string;
}

export default function RegisterPage() {
  const { register, loading, error, clearError } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [teamId, setTeamId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    clearError();
    // Fetch teams
    api.get<any>('/teams').then((data) => {
      if (data.teams) setTeams(data.teams);
    }).catch(() => {});
  }, []);

  const getPasswordStrength = (): { label: string; color: string; width: string } => {
    if (!password) return { label: '', color: '', width: '0%' };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;

    const levels = [
      { label: 'Weak', color: 'var(--error)', width: '25%' },
      { label: 'Fair', color: 'var(--warning)', width: '50%' },
      { label: 'Good', color: 'var(--info)', width: '75%' },
      { label: 'Strong', color: 'var(--success)', width: '100%' },
    ];
    return levels[Math.max(0, score - 1)] || levels[0];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (!email.startsWith('v-')) {
      setLocalError('Email must start with "v-" prefix');
      return;
    }
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }
    if (!teamId) {
      setLocalError('Please select a team');
      return;
    }

    await register({ firstName, lastName, email, teamId, password });
  };

  const strength = getPasswordStrength();

  return (
    <div className="login-page">
      <div className="login-card glass-card" style={{ maxWidth: 500 }}>
        <div className="login-card__logo">
          <div className="login-card__logo-icon">
            <Zap size={48} style={{ color: '#ff2a2a', filter: 'drop-shadow(0 0 12px rgba(255, 42, 42, 0.8))' }} />
          </div>
          <h1 className="login-card__title">Create Account</h1>
          <p className="login-card__subtitle">Join Premier Case Tracker</p>
        </div>

        {(error || localError) && (
          <div style={{
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--error-bg)',
            border: '1px solid rgba(255,61,61,0.3)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--error)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--space-4)',
          }}>
            {localError || error}
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit} autoComplete="off">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="reg-firstname">First Name</label>
              <input id="reg-firstname" className="glass-input" value={firstName}
                onChange={(e) => setFirstName(e.target.value)} required placeholder="John" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="reg-lastname">Last Name</label>
              <input id="reg-lastname" className="glass-input" value={lastName}
                onChange={(e) => setLastName(e.target.value)} required placeholder="Doe" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="reg-email">Email</label>
            <input id="reg-email" type="email" className="glass-input" value={email}
              onChange={(e) => setEmail(e.target.value)} required placeholder="v-john.doe@company.com" autoComplete="off" />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="reg-team">Team</label>
            <select id="reg-team" className="glass-select" value={teamId} onChange={(e) => setTeamId(e.target.value)} required>
              <option value="">Select a team...</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="reg-password">Password</label>
            <input id="reg-password" type="password" className="glass-input" value={password}
              onChange={(e) => setPassword(e.target.value)} required placeholder="Min. 8 characters" autoComplete="new-password" />
            {password && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-1)' }}>
                <div style={{ flex: 1, height: 4, background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-pill)', overflow: 'hidden' }}>
                  <div style={{ width: strength.width, height: '100%', background: strength.color, borderRadius: 'var(--radius-pill)', transition: 'all 300ms ease' }} />
                </div>
                <span style={{ fontSize: 'var(--font-size-xs)', color: strength.color, fontWeight: 600 }}>{strength.label}</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="reg-confirm">Confirm Password</label>
            <input id="reg-confirm" type="password" className="glass-input" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} required placeholder="Re-enter password" autoComplete="new-password" />
          </div>

          <button type="submit" className="glass-btn glass-btn--primary glass-btn--lg"
            disabled={loading} style={{ width: '100%', marginTop: 'var(--space-2)' }}>
            {loading ? <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : 'Create Account'}
          </button>

          <p style={{ textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
            Already have an account? <Link to="/login" style={{ fontWeight: 600 }}>Sign In</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
